import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useUserLocation } from '../components/hooks/useUserLocation';
import { calculateDistance, calculateBearing, getDirectionFromBearing } from '../components/utils/locationUtils';
import placesData from '../components/data/SK.json';
import '../components/css/ResultsPage.css';
import Sort from '../components/Sort';
import OpenStatus from '../components/OpenStatus';
import StarRating from '../components/StarRating';
import Header2 from '../components/Header2';
import Maps from '../components/Maps';
import { useUser } from '../components/UserContext'; // Use the UserProvider
import { getFirestore, getDoc, doc, updateDoc, arrayUnion, arrayRemove } from 'firebase/firestore';
import keywordToCategoryData from '../components/keywords.json';

function ResultsPage() {
    const [places, setPlaces] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortOption, setSortOption] = useState('recommended'); // Default sorting option
    const [favorites, setFavorites] = useState([]);
    const location = useLocation();
    const { userLocation, error } = useUserLocation();
    const { user } = useUser(); // Access the logged-in user
    const db = getFirestore();

    const keywordToCategory = { ...keywordToCategoryData };

    const getSearchQuery = () => {
        const params = new URLSearchParams(location.search);
        return params.get('search') || '';
    };

    useEffect(() => {
        if (!user) {
            return;
        }

        const fetchFavorites = async () => {
            try {
                const userRef = doc(db, 'Users', user.uid);
                const userSnap = await getDoc(userRef);

                if (userSnap.exists()) {
                    const userData = userSnap.data();
                    setFavorites(userData.favorites || []);
                }
            } catch (error) {
                console.error('Error fetching favorites:', error);
                alert('An error occurred while fetching your favorites.');
            }
        };

        fetchFavorites();
    }, [user, db]);

    useEffect(() => {
        if (!user) {
            return;
        }

        const normalizeString = (str) =>
            str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

        const query = normalizeString(getSearchQuery());
        setLoading(true);

        const filteredPlaces = placesData
            .filter((place) => favorites.includes(place.place_id)) // Only include favorites
            .filter((place) => {
                // Map the query to a category if it matches a keyword
                const mappedCategory = keywordToCategory[normalizeString(query)] || query;

                return (
                    (place.name && normalizeString(place.name).includes(mappedCategory)) ||
                    (place.types && place.types.some(type => normalizeString(type).includes(mappedCategory)))
                );
            })
            .map(place => {
                if (userLocation) {
                    const distance = calculateDistance(
                        userLocation.latitude,
                        userLocation.longitude,
                        place.latitude,
                        place.longitude
                    );

                    const bearing = calculateBearing(
                        userLocation.latitude,
                        userLocation.longitude,
                        place.latitude,
                        place.longitude
                    );

                    const direction = getDirectionFromBearing(bearing);

                    return { ...place, distance: Math.round(distance), direction };
                }
                return place;
            })
            .sort((a, b) => {
                switch (sortOption) {
                    case 'closest':
                        if (a.distance !== undefined && b.distance !== undefined) {
                            return a.distance - b.distance;
                        }
                        return 0;
                    case 'highestRated':
                        if (a.rating !== b.rating) {
                            return b.rating - a.rating; // Higher rating first
                        }
                        return b.user_ratings_total - a.user_ratings_total; // More reviews first
                    case 'recommended':
                    default:
                        const statusA = OpenStatus({ openingHours: a.opening_hours, forSorting: true });
                        const statusB = OpenStatus({ openingHours: b.opening_hours, forSorting: true });
                        const priority = { open: 1, closed: 2, "no-hours": 3 };

                        if (priority[statusA] !== priority[statusB]) {
                            return priority[statusA] - priority[statusB];
                        }

                        if (a.distance !== undefined && b.distance !== undefined) {
                            return a.distance - b.distance;
                        }

                        if (a.premium === "1" && b.premium !== "1") {
                            return -1;
                        }
                        if (a.premium !== "1" && b.premium === "1") {
                            return 1;
                        }

                        return 0;
                }
            });

        setPlaces(filteredPlaces);
        setLoading(false);
    }, [location.search, userLocation, sortOption, favorites]);

    const handleFavorite = async (placeId) => {
        if (!user) {
            alert('You must be logged in to favorite a place.');
            return;
        }

        try {
            const userRef = doc(db, 'Users', user.uid);
            if (favorites.includes(placeId)) {
                // Remove from favorites
                await updateDoc(userRef, {
                    favorites: arrayRemove(placeId),
                });
                setFavorites(favorites.filter((id) => id !== placeId));

            } else {
                // Add to favorites
                await updateDoc(userRef, {
                    favorites: arrayUnion(placeId),
                });
                setFavorites([...favorites, placeId]);
            }
        } catch (error) {
            console.error('Error updating favorites:', error);
            alert('An error occurred while updating favorites.');
        }
    };

    return (
        <>
            <Header2 />
            <Maps places={places} />
            <div className='shadow'></div>
            <div className="results-page">

                <div className="content">
                    <Sort setSortOption={setSortOption} />
                    <p>Obľúbené: {places.length}</p>
                    {error && <p>Error fetching location: {error.message}</p>}
                    <br />
                    {places.length === 0 && !loading ? (
                        <p>Nemáte žiadne obľúbené položky</p>
                    ) : (
                        <div className="products-grid">
                            {places.map((place) => (
                                <div
                                    className={`product-tile ${place.premium === "1" ? "premium-border" : ""}`}
                                    key={place.place_id}
                                    style={{ position: "relative" }}
                                >
                                    {place.premium === '1' && (
                                        <div className="recommended-box">
                                            <i className="fa-solid fa-check"></i> Overený užívateľ
                                        </div>
                                    )}
                                    <div
                                        className="tile-image"
                                        style={{
                                            backgroundImage: `url(${place.short_photo_references && place.short_photo_references.length > 0
                                                ? `/images/${place.short_photo_references[0]}.jpg`
                                                : `/images/default.png`})`
                                        }}
                                    ></div>

                                    <div className="tile-content">
                                        <h2>                                            <Link to={`/catalog/${place.place_id}`}>
                                            {place.name.length > 40 ? `${place.name.substring(0, 40)}...` : place.name}
                                        </Link></h2>
                                        <p><a href={place.google_maps_url} target="_blank" rel="noopener noreferrer">{place.address.length > 40 ? `${place.address.substring(0, 40)}...` : place.address}</a></p>
                                        <br />
                                        <div className='place-rating'>
                                            <StarRating rating={place.rating} />
                                            <p>Hodnotilo: {place.user_ratings_total}</p>
                                        </div>

                                        {place.distance !== undefined && (
                                            <p>
                                                <i className="fas fa-map-marker-alt" aria-hidden="true"></i>{place.distance} meters
                                                <span> {place.direction}</span>
                                            </p>
                                        )}
                                        <OpenStatus openingHours={place.opening_hours} />
                                    </div>
                                    <div
                                        className="favorite-button"
                                        onClick={() => handleFavorite(place.place_id)}
                                    >
                                        <i
                                            className={`fa-regular fa-star ${favorites.includes(place.place_id) ? "yellow-star" : "gray-star"}`}
                                        />

                                    </div>
                                    <div className="tile-buttons">
                                        <button
                                            className="contact-button"
                                            onClick={() => window.location.href = `tel:${place.phone_number}`}
                                        >
                                            <i className="fa-solid fa-phone"></i>
                                            <br />
                                            Zavolať
                                        </button>

                                        {/* Conditionally render the "Rezervovať" button and adjust the layout */}
                                        {place.reservation_url && place.reservation_url !== '' && (

                                            <button
                                                onClick={() => window.location.href = place.reservation_url}
                                                className="contact-button"
                                            >
                                                <i className="fa-solid fa-calendar"></i>
                                                <br />
                                                <br />
                                                Rezervovať
                                            </button>

                                        )}
                                    </div>

                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}

export default ResultsPage;
